import {
  formatFraction,
  formatStoredFraction,
  fractionToPercent,
  percentToFraction,
} from './pricing-format';

/**
 * Business names and units for the pricing parameters the backend defines.
 * A '%' parameter is stored as a fraction (0.05 = 5%) and shown and edited as a percent.
 */

const PARAMETERS: Record<string, { label: string; unit: string; help: string }> = {
  FxBufferPct: {
    label: 'FX buffer',
    unit: '%',
    help: 'Added to the exchange rate to absorb movement.',
  },
  TargetMarginPct: { label: 'Target margin', unit: '%', help: 'Margin aimed for above cost.' },
  FloorPct: { label: 'Price floor', unit: '%', help: 'Lowest allowed margin over cost.' },
  CeilingPct: { label: 'Price ceiling', unit: '%', help: 'Highest allowed margin over cost.' },
  RoundingIncrement: { label: 'Rounding step', unit: '', help: 'Prices round to this step.' },
  MaximumFxAge: { label: 'Maximum FX age', unit: 'h', help: 'Older rates block pricing.' },
  MgmtFeePct: { label: 'Management fee', unit: '%', help: 'Platform fee on each sale.' },
  PartnerSharePct: { label: 'Partner share', unit: '%', help: 'Share passed to the partner.' },
};

export function parameterLabel(parameter: string): string {
  return PARAMETERS[parameter]?.label ?? parameter;
}

export function parameterUnit(parameter: string): string {
  return PARAMETERS[parameter]?.unit ?? '';
}

export function parameterHelp(parameter: string): string {
  return PARAMETERS[parameter]?.help ?? '';
}

export function formatParameter(parameter: string, value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const unit = parameterUnit(parameter);
  return unit === '%' ? formatFraction(value) : unit === 'h' ? `${value} h` : String(value);
}

/** The stored fraction of a '%' parameter ("0.05"), shown beside its percent; null for other units. */
export function storedParameter(
  parameter: string,
  value: number | null | undefined,
): string | null {
  if (value === null || value === undefined || parameterUnit(parameter) !== '%') return null;
  return formatStoredFraction(value);
}

export interface PercentEntryCheck {
  message: string;
  /** The percent staff probably meant, offered as a one-click fix. */
  suggestion: number | null;
}

/**
 * A typed percent that looks like a misplaced decimal point: written as a fraction
 * (0.1 for 10%) or ten times or more away from the live value (0.1 for 1%). It
 * warns and does not block, because a small or large change can be intended.
 * Both arguments are percents, as typed.
 */
export function checkPercentEntry(
  parameter: string,
  typed: number,
  livePercent: number | undefined,
): PercentEntryCheck | null {
  if (parameterUnit(parameter) !== '%' || !(typed > 0)) return null;
  const messages: string[] = [];

  if (livePercent !== undefined && livePercent > 0 && typed !== livePercent) {
    const ratio = typed / livePercent;
    if (ratio >= 10) messages.push(`${Math.round(ratio)}× higher than live (${livePercent}%).`);
    if (ratio <= 0.1) messages.push(`${Math.round(1 / ratio)}× lower than live (${livePercent}%).`);
  }

  // 0.1 typed as a fraction means 10%.
  const suggestion = typed < 1 ? fractionToPercent(typed) : null;
  if (suggestion !== null) messages.push(`Under 1% — did you mean ${suggestion}%?`);

  return messages.length > 0 ? { message: messages.join(' '), suggestion } : null;
}

/** The stored value in the unit staff type: 0.05 → 5 for a '%' parameter. */
export function toInputValue(parameter: string, value: number): number {
  return parameterUnit(parameter) === '%' ? fractionToPercent(value) : value;
}

/** What staff typed back to the stored value: 5 → 0.05 for a '%' parameter. */
export function fromInputValue(parameter: string, input: number): number {
  return parameterUnit(parameter) === '%' ? percentToFraction(input) : input;
}
